// ═══════════════════════════════════════════════════════════════
// useUserPref – felhasználónként mentett megjelenítési beállítás
//
// A szerveren tárolódik (GET/PUT /api/me/ui-prefs), így más böngészőben és gépen is érvényes.
// A localStorage csak gyorsítótár: a felület azonnal a legutóbbi értékkel jelenik meg,
// majd a szerver értéke felülírja. Hálózati hibánál a helyi érték marad.
// ═══════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import { ApiClient } from '../api/client';

const CACHE_KEY = 'kozad_ui_prefs';
let serverPrefs: Record<string, unknown> | null = null;
let loading: Promise<Record<string, unknown>> | null = null;
const listeners = new Set<() => void>();

const readCache = (): Record<string, unknown> => {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}') || {}; } catch { return {}; }
};
const writeCache = (p: Record<string, unknown>) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(p)); } catch { /* privát mód: csak a szerver */ }
};

function loadOnce() {
  if (serverPrefs) return Promise.resolve(serverPrefs);
  if (!loading) {
    loading = ApiClient.getUiPrefs().then((r) => {
      serverPrefs = r.ok && r.data && typeof r.data === 'object' ? { ...readCache(), ...r.data } : readCache();
      writeCache(serverPrefs);
      listeners.forEach((l) => l());
      return serverPrefs;
    }).catch(() => { loading = null; return readCache(); });
  }
  return loading;
}

/** kijelentkezéskor / felhasználóváltáskor */
export function resetUserPrefs() { serverPrefs = null; loading = null; try { localStorage.removeItem(CACHE_KEY); } catch { /* */ } }

export function useUserPref<T>(key: string, fallback: T): [T, (v: T) => void] {
  const pick = () => {
    const src = serverPrefs || readCache();
    return (key in src ? (src[key] as T) : fallback);
  };
  const [value, setValue] = useState<T>(pick);
  useEffect(() => {
    const l = () => setValue(pick());
    listeners.add(l);
    loadOnce();
    return () => { listeners.delete(l); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const set = useCallback((v: T) => {
    serverPrefs = { ...(serverPrefs || readCache()), [key]: v };
    writeCache(serverPrefs);
    listeners.forEach((l) => l());
    ApiClient.saveUiPrefs({ [key]: v }).catch(() => { /* a helyi érték megmarad */ });
  }, [key]);
  return [value, set];
}
